// frontend/src/components/Layout/UnifiedNavbar.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import {
  BookmarkIcon,
  UserCircleIcon,
  Bars3Icon,
  XMarkIcon,
  ChatBubbleLeftEllipsisIcon,
  NewspaperIcon,
  HomeIcon,
  InformationCircleIcon,
  ListBulletIcon,
  ChartPieIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import logo from '../../assets/logo.svg';

// Helper for smooth scroll (if used for landing page sections)
const smoothScrollTo = (elementId) => {
    const element = document.getElementById(elementId);
    if (element) {
        element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });
    } else {
        console.warn(`Smooth scroll target '${elementId}' not found.`);
    }
};

const UnifiedNavbar = () => {
    const { isAuthenticated, logout, currentUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname]);

    const handleLogout = async () => {
        await logout();
        // navigate('/login'); // Usually not needed here, AuthProvider handles state change
    };

    const getDisplayName = () => {
        if (!currentUser) return 'User';
        if (currentUser.first_name && currentUser.last_name) return `${currentUser.first_name} ${currentUser.last_name}`;
        if (currentUser.first_name) return currentUser.first_name;
        if (currentUser.username && currentUser.username !== currentUser.email) return currentUser.username;
        if (currentUser.email) return currentUser.email.split('@')[0];
        return 'User';
    };
    const displayName = getDisplayName();

    // Function to get user's initial for the avatar circle
    const getUserInitial = () => {
        if (!currentUser) return 'U';
        
        // Priority: first letter of email, last name, first name, username
        if (currentUser.email) return currentUser.email.charAt(0).toUpperCase();
        if (currentUser.last_name) return currentUser.last_name.charAt(0).toUpperCase();
        if (currentUser.first_name) return currentUser.first_name.charAt(0).toUpperCase();
        if (currentUser.username) return currentUser.username.charAt(0).toUpperCase();
        return 'U';
    };

    // Function to generate avatar background color based on user initial
    const getAvatarColor = () => {
        const initial = getUserInitial();
        const colors = [
            'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-red-500', 
            'bg-yellow-500', 'bg-indigo-500', 'bg-pink-500', 'bg-teal-500'
        ];
        const charCode = initial.charCodeAt(0);
        return colors[charCode % colors.length];
    };

    const publicNavLinks = [
        { href: "/#features", label: "Features", isScrollLink: true, id: "features", icon: HomeIcon },
        { href: "/#sources-landing", label: "Sources", isScrollLink: true, id: "sources-landing", icon: InformationCircleIcon },
    ];

    const privateNavLinks = [
        { href: "/article-feed", label: "Article Feed", icon: NewspaperIcon },
        { href: "/daily-digests", label: "Daily Digests", icon: CalendarDaysIcon },
        // { href: "/ai-analyst", label: "AI Analyst", icon: ChatBubbleLeftEllipsisIcon },
        // { href: "/topic-insights", label: "Topic Insights", icon: ChartPieIcon },
        // REMOVED: Sources link to hide data sources from users
        // { href: "/sources", label: "Sources", icon: ListBulletIcon },
        { href: "/saved-articles", label: "Saved Items", icon: BookmarkIcon },
    ];

    const handleLinkClick = (e, href, isScroll = false, targetId = '') => {
        if (isScroll) {
            e.preventDefault();
            if (location.pathname !== '/') {
                navigate('/');
                setTimeout(() => smoothScrollTo(targetId), 100);
            } else {
                smoothScrollTo(targetId);
            }
        }
        if (mobileMenuOpen) {
            setMobileMenuOpen(false);
        }
    };

    const renderNavItem = (link, isMobile = false) => {
        const commonClasses = `flex items-center transition duration-150 ease-in-out text-sm ${
            isMobile
                ? "px-6 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 w-full text-left rounded-md"
                : "text-gray-600 hover:text-blue-600 px-3 py-2 rounded-md"
        }`;
        const IconComponent = link.icon;
        const isActive = !link.isScrollLink && location.pathname === link.href;
        const activeClasses = isActive ? (isMobile ? "bg-blue-50 text-blue-700 font-semibold" : "text-blue-700 font-semibold") : "";

        if (link.isScrollLink) {
            return (
                <a key={link.label} href={link.href} onClick={(e) => handleLinkClick(e, link.href, true, link.id)} className={`${commonClasses} ${activeClasses} cursor-pointer`}>
                    {IconComponent && <IconComponent className={`h-5 w-5 ${isMobile ? 'mr-3' : 'mr-1.5 text-gray-400 group-hover:text-blue-500'}`} />}
                    {link.label}
                </a>
            );
        }
        return (
            <Link key={link.label} to={link.href} onClick={(e) => handleLinkClick(e, link.href, false, '')} className={`${commonClasses} ${activeClasses} group`}>
                {IconComponent && <IconComponent className={`h-5 w-5 ${isMobile ? 'mr-3' : 'mr-1.5 text-gray-400 group-hover:text-blue-500'} ${isActive ? 'text-blue-600' : ''}`} />}
                {link.label}
            </Link>
        );
    };

    return (
        <nav className="bg-white py-3 fixed w-full z-40 shadow-sm border-b border-gray-200 print:hidden rounded-lg">
            <div className="container mx-auto px-4 sm:px-6 flex justify-between items-center">
                <Link to="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center space-x-2 hover:opacity-90 transition-opacity">
                    <img src={logo} alt="Agent Digest" className="h-8 md:h-10 w-auto" />
                    <span className="text-xl md:text-2xl font-bold text-blue-600">Agent Digest</span>
                </Link>

                <div className="hidden md:flex space-x-1 lg:space-x-2 items-center">
                    {(isAuthenticated ? privateNavLinks : publicNavLinks).map(link => renderNavItem(link, false))}
                </div>

                <div className="hidden md:flex items-center space-x-3">
                    {isAuthenticated ? (
                        <>
                            <Link 
                                to="/account"
                                className={`w-8 h-8 rounded-full ${getAvatarColor()} flex items-center justify-center text-white font-semibold text-sm hover:opacity-80 transition-opacity cursor-pointer`}
                                title={`${displayName} - Go to Account`}
                            >
                                {getUserInitial()}
                            </Link>
                            <button onClick={handleLogout} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-150 ease-in-out shadow-sm hover:shadow-md text-sm">
                                Logout
                            </button>
                        </>
                    ) : (
                        <>
                            <Link to="/login" className="text-gray-600 hover:text-blue-600 transition duration-150 text-sm font-medium px-3 py-2 rounded-md">
                                Log In
                            </Link>
                            <Link to="/register" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-5 rounded-lg transition duration-150 ease-in-out shadow-md hover:shadow-lg text-sm">
                                Get Started
                            </Link>
                        </>
                    )}
                </div>

                <div className="md:hidden">
                    <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-gray-600 hover:text-blue-600 focus:outline-none p-2 rounded-md" aria-label="Toggle mobile menu" aria-expanded={mobileMenuOpen}>
                        {mobileMenuOpen ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            {mobileMenuOpen && (
                <div className="md:hidden bg-white shadow-lg py-2 absolute w-full border-t border-gray-200">
                    <div className="flex flex-col px-2 space-y-1">
                        {(isAuthenticated ? privateNavLinks : publicNavLinks).map(link => renderNavItem(link, true))}
                        <hr className="my-2 border-gray-100" />
                        {isAuthenticated ? (
                            <>
                                <Link 
                                    to="/account"
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="flex items-center px-6 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 text-sm transition-colors rounded-md"
                                >
                                    <div 
                                        className={`w-6 h-6 rounded-full ${getAvatarColor()} flex items-center justify-center text-white font-semibold text-xs mr-3`}
                                        title={displayName}
                                    >
                                        {getUserInitial()}
                                    </div>
                                    {displayName}
                                </Link>
                                <button onClick={async () => { await handleLogout(); setMobileMenuOpen(false); }} className="w-full text-left px-6 py-3 text-blue-600 hover:bg-blue-50 hover:text-blue-700 font-medium rounded-md text-sm">
                                    Logout
                                </button>
                            </>
                        ) : (
                            <>
                                <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block px-6 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md text-sm">
                                    Log In
                                </Link>
                                <Link to="/register" onClick={() => setMobileMenuOpen(false)} className="block mx-4 my-2 px-6 py-3 text-white bg-blue-600 hover:bg-blue-700 text-center font-semibold rounded-lg text-sm">
                                    Get Started
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            )}
        </nav>
    );
};

export default UnifiedNavbar;
